import React, { useState } from 'react';
import api from '../../api/api.js';
import showToast from '../ShowToast';
import { Sparkles, Undo2, Loader2 } from 'lucide-react';

const JobDescriptionEnhancer = ({
    description,
    professionName,
    onDescriptionChange,
    onTitleChange,
    textareaClassName,
    placeholder,
    rows = 5
}) => {
    const [isEnhancing, setIsEnhancing] = useState(false);
    const [previousDescription, setPreviousDescription] = useState(null);
    const [previousTitle, setPreviousTitle] = useState(null);
    const [wasEnhanced, setWasEnhanced] = useState(false);

    const handleEnhance = async () => {
        if (!description || description.trim().length < 15) {
            showToast('error', 'Please write at least a short description before enhancing.');
            return;
        }

        setPreviousDescription(description);
        // Store current title for undo — onTitleChange will be called with the new suggestion
        setPreviousTitle(null); // Will be set via the callback pattern below
        setIsEnhancing(true);

        try {
            const res = await api.post('/api/Jobs/enhance-description', {
                description: description,
                professionName: professionName || ''
            });

            const { enhancedDescription, suggestedTitle } = res.data;

            if (enhancedDescription) {
                onDescriptionChange(enhancedDescription);
                setWasEnhanced(true);
            }

            if (suggestedTitle && onTitleChange) {
                // Store the title that was there before we change it
                setPreviousTitle('__CAPTURE__'); // Signal that we changed the title
                onTitleChange(suggestedTitle);
            }

            showToast('success', 'Description enhanced by AI!');
        } catch (err) {
            console.error('AI Enhance Error:', err);
            showToast('error', 'Failed to enhance description. Please try again.');
        } finally {
            setIsEnhancing(false);
        }
    };

    const handleUndo = () => {
        if (previousDescription !== null) {
            onDescriptionChange(previousDescription);
            setPreviousDescription(null);
            setWasEnhanced(false);
            showToast('info', 'Restored original description.');
        }
    };

    const canEnhance = description && description.trim().length >= 15 && !isEnhancing;

    return (
        <div>
            <textarea
                id="description"
                placeholder={placeholder}
                value={description}
                onChange={e => onDescriptionChange(e.target.value)}
                className={textareaClassName}
                rows={rows}
            />

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '8px'
            }}>
                <button
                    type="button"
                    onClick={handleEnhance}
                    disabled={!canEnhance}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: canEnhance ? '#7c3aed' : '#9ca3af',
                        backgroundColor: canEnhance ? '#f5f3ff' : '#f9fafb',
                        border: `1px solid ${canEnhance ? '#c4b5fd' : '#e5e7eb'}`,
                        borderRadius: '6px',
                        cursor: canEnhance ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s'
                    }}
                >
                    {isEnhancing ? (
                        <>
                            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                            Enhancing...
                        </>
                    ) : (
                        <>
                            <Sparkles size={16} />
                            Enhance with AI
                        </>
                    )}
                </button>

                {wasEnhanced && previousDescription !== null && (
                    <button
                        type="button"
                        onClick={handleUndo}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            color: '#6b7280',
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Undo2 size={14} />
                        Undo
                    </button>
                )}

                {wasEnhanced && (
                    <span style={{
                        fontSize: '0.8rem',
                        color: '#7c3aed',
                        fontWeight: '500'
                    }}>
                        ✨ AI enhanced
                    </span>
                )}
            </div>

            <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
                Tip: Write a rough description, then click "Enhance with AI" to polish it into a professional brief.
            </p>
        </div>
    );
};

export default JobDescriptionEnhancer;
